import React, { Component } from 'react'
import styled from '@emotion/styled'
import { rgba } from 'polished'

import { colors, typography, animations, util } from 'src/styles'
import { buttonThemes as themes } from 'src/styles/themes'
import MaterialIcon from 'src/components/MaterialIcon'

import Link from 'src/components/Link'

const buttonSizes = {
	tiny: '30px',
	small: '40px',
	medium: '50px',
	large: '60px'
}

const buttonSettings = {
	radius: '0px',
	border: '2px solid',
	transitionSpeed: animations.mediumSpeed,
	verticalOffset: '1px'
}

const getState = (loading, error, success, disabled) => {
	let buttonState = ''
	if (error) {
		buttonState = 'error'
	} else if (loading) {
		buttonState = 'loading'
	} else if (success) {
		buttonState = 'success'
	} else if (disabled) {
		buttonState = 'disabled'
	}

	return buttonState
}

const setButtonTheme = (theme, state) => `
	color: ${ themes[theme].color };
	background: ${ themes[theme].background };
	${ theme.shadow ? `
		box-shadow: ${ theme.shadow };
	` : `
		box-shadow: none;
	` }
	${ themes[theme].borderColor ? `
		border-color: ${ themes[theme].borderColor };
	` : `
		border-color: ${ themes[theme].background };
	` }
	&:hover {
		${ !state ? `
			color: ${ themes[theme].hoverColor };
			background: ${ themes[theme].hoverBackground };
			${ themes[theme].borderHoverColor ? `
				border-color: ${ themes[theme].borderHoverColor };
			` : `
				border-color: ${ themes[theme].hoverBackground };
			` }
			${ theme.hoverShadow ? `
				box-shadow: ${ theme.hoverShadow };
			` : `` }
		` : `` }
	}
`

const DisabledButtonStyles = theme => `
	&[disabled],
	&:disabled {
		background: ${ themes[theme] && themes[theme].hoverBackground !== 'transparent' ? rgba(themes[theme].hoverBackground, 0.2) : rgba(colors.green, 0.2) };
		border-color: transparent;
		color: ${ themes[theme] && themes[theme].color !== 'transparent' ? rgba(themes[theme].color, 0.7) : rgba(colors.green, 0.7) };
		cursor: not-allowed;
	}
`

const ButtonIcon = styled.div`
	${ ({ position }) => position === 'left' ? `
		margin-right: .5em;
	` : `
		margin-left: .5em;
	` }
	span,
	svg {
		display: block;
	}
`

const ButtonStyles = (state, shape, size, theme) => (`
	appearance: none;
	-webkit-tap-highlight-color: rgba(0,0,0,0);
	-webkit-touch-callout: none;
	outline: none;
	cursor: pointer;
	display: inline-block;
	vertical-align: middle;
	border: ${ buttonSettings.border };
	height: ${ buttonSizes[size] };
	padding: 0 calc(${ buttonSizes[size] } * .5) ${ buttonSettings.verticalOffset };
	min-width: calc(${ buttonSizes[size] } * 3.35);
	text-transform: none;
	letter-spacing: 0;
	border-radius: ${ buttonSettings.radius };
	text-align: center;
	${ typography.buttonStyle }
	line-height: 1em;
	${ util.fontSmoothing }
	transition: background ${ buttonSettings.transitionSpeed } ease-in-out,
							color ${ buttonSettings.transitionSpeed } ease-in-out,
							border ${ buttonSettings.transitionSpeed } ease-in-out,
							box-shadow ${ buttonSettings.transitionSpeed } ease-in-out,
							transform ${ buttonSettings.transitionSpeed } ease-in-out,
							opacity ${ buttonSettings.transitionSpeed } ease-in-out;
	// Button States
	${ state === 'loading' ? `cursor: wait;` : `` }
	${ state === 'error' || state === 'success' ? `cursor: default;` : `` }

	// Button Shapes
	${ shape ? `
		${ shape.includes('circle') || shape.includes('square') ? `
			padding: 0 !important;
			width: ${ buttonSizes[size] };
			min-width: ${ buttonSizes[size] };
			${ ButtonIcon } {
				margin: 0;
			}
		` : `` }
	` : `` }
	${ shape && shape.includes('circle') ? `border-radius: 50%;` : `` }

	// Button Themes
	${ setButtonTheme(theme, state) }
	${ state === 'disabled' ? `${ DisabledButtonStyles(theme) }` : `` }

	// Button Size Tweaks
	${ size === 'small' ? `
		${ util.responsiveStyles('font-size', 11, 11, 11, 11) }
	` : `` }
	${ size === 'tiny' ? `
		${ util.responsiveStyles('font-size', 11, 11, 11, 11) }
	` : `` }

`)

const ButtonContent = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	width: 100%;
	svg {
		* {
			fill: currentcolor;
		}
	}
`

const StyledButtonLink = styled(Link)`
	${ ({ loading, error, success, disabled, shape, size, theme }) => ButtonStyles(getState(loading, error, success, disabled), shape, size, theme) }
`

const StyledButtonElement = styled.button`
	${ ({ loading, error, success, disabled, shape, size, theme }) => ButtonStyles(getState(loading, error, success, disabled), shape, size, theme) }
`

class Button extends Component {
	renderIcon = (icon, position, shape, size) => {
		let renderedIcon = false
		if (typeof icon === 'string') {
			renderedIcon = <ButtonIcon size={size} position={position} shape={shape}><MaterialIcon size={this.props.size === 'tiny' && '18px'}>{icon}</MaterialIcon></ButtonIcon>
		} else {
			renderedIcon = <ButtonIcon size={size} position={position} shape={shape}>{icon}</ButtonIcon>
		}
		return renderedIcon
	}

	renderButtonContent = () => {
		const { loading, error, success, children, label, icon, iconPosition, shape, size } = this.props
		if (loading) {
			return <ButtonContent className="button-content">
				...
			</ButtonContent>
		} else if (error) {
			return <ButtonContent className="button-content">
				error
			</ButtonContent>
		} else if (success) {
			return <ButtonContent className="button-content">
				success
			</ButtonContent>
		} else {
			return <ButtonContent className="button-content">
				{icon && iconPosition !== 'right' ? this.renderIcon(icon, iconPosition, shape, size) : false}
				{children || label}
				{icon && iconPosition === 'right' ? this.renderIcon(icon, iconPosition, shape, size) : false}
			</ButtonContent>
		}
	}

	render () {
		const {
			to,
			external,
			target,
			icon,
			iconPosition,
			loading,
			error,
			success,
			disabled,
			onClick,
			setTheme,
			theme,
			className,
			shape,
			size,
			title,
			name
		} = this.props

		if (to) {
			return (
				<StyledButtonLink
					className={'button ' + className}
					to={to}
					target={target}
					external={external}
					icon={icon}
					iconPosition={iconPosition}
					loading={loading}
					error={error}
					success={success}
					disabled={disabled}
					onClick={onClick}
					theme={theme || setTheme}
					shape={shape}
					size={size}
					title={title || name}
					name={title || name}
					aria-label={title || name}
					rel={external ? 'noopener noreferrer' : ''}
				>
					{this.renderButtonContent()}
				</StyledButtonLink>
			)
		} else {
			return (
				<StyledButtonElement
					className={className}
					icon={icon}
					iconPosition={iconPosition}
					loading={loading}
					error={error}
					success={success}
					disabled={disabled}
					onClick={onClick}
					theme={theme || setTheme}
					shape={shape}
					size={size}
					title={title || name}
					name={title || name}
					aria-label={title || name}
				>
					{this.renderButtonContent()}
				</StyledButtonElement>
			)
		}
	}
}

Button.defaultProps = {
  setTheme: 'default',
	size: 'medium',
	shape: 'default',
	iconPosition: 'left'
}

export default Button
